import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon, images } from "@core/assets";
import { cn } from "@core/utils";
import { AccountMenu } from "./AccountMenu";
import type { NavGroup, NavItem } from "./types";

interface SidebarContentProps {
  /** Labelled nav sections. A group with no items renders nothing, heading included. */
  groups: NavGroup[];
  /** Nav pinned to the bottom above the account card. */
  footerItems?: NavItem[];
  /** Called after a nav link is activated — the mobile drawer uses it to close itself. */
  onNavigate?: () => void;
  className?: string;
}

// Design (Figma 325:23203 Sidebar): px 12 / py 8, 12px gap, 20px icon, radius/sm, sm/Medium on
// gray/700. Active = brand/50 fill + brand/700 sm/SemiBold.
// `py-3 lg:py-2` is mobile-first, not a fudge: 12px gives the 44px touch target the drawer needs
// at 320px, and the design's 8px applies from lg where the sidebar is pointer-driven.
const NAV_LINK = cn(
  "flex items-center gap-sm rounded-sm px-sm py-3 text-sm-medium lg:py-2",
  "text-(--action-secondary-fg) transition-colors duration-200 ease-out",
  "hover:bg-gray-50 pressed:bg-gray-100",
  "outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
  "data-[status=active]:bg-brand-50 data-[status=active]:font-semibold",
  "data-[status=active]:text-(--text-link)"
);

function NavLinks({
  items,
  onNavigate,
}: {
  items: NavItem[];
  onNavigate?: () => void;
}) {
  const { t } = useTranslation("common");

  return (
    <ul className="flex flex-col gap-xxs">
      {items.map((item) => (
        <li key={item.to}>
          {/* Active state comes from TanStack Router's own `data-status="active"` — no
              hand-rolled pathname comparison; the router already knows what's matched.
              `exact: true` matters now that a nav item can carry an `expanded` sub-rail with its
              own active tabs (Figma 09.1's sidebar): fuzzy matching would ALSO mark « Appels »
              active while a sub-tab three levels deeper is the one actually selected, which the
              design does not show — only the sub-tab gets the brand highlight. */}
          <Link
            to={item.to}
            activeOptions={{ exact: true }}
            onClick={onNavigate}
            className={NAV_LINK}
          >
            <Icon name={item.icon} size={20} className="shrink-0" />
            <span className="truncate">{t(item.labelKey)}</span>
          </Link>
          {item.expanded}
        </li>
      ))}
    </ul>
  );
}

/**
 * The sidebar's inner column, shared verbatim by the desktop `<aside>` and the mobile drawer so
 * the two can never drift: brand → nav → spacer → footer nav → account card.
 *
 * Nav arrives as LABELLED GROUPS (Figma 356:12747), which is how the design organises it — a
 * heading naming the product module, then its screens. The route layer decides which groups and
 * which entries exist; core renders whatever it is handed and nothing when handed nothing.
 *
 * The admin side is still being rebuilt, so the list is deliberately short — see
 * `routes/-staff-nav.ts` for what is registered and what is declared-but-off.
 *
 * The command-palette search field that used to sit under the logo is gone: the design moved it
 * into the page toolbar, where it belongs to a screen rather than to the chrome.
 */
export function SidebarContent({
  groups,
  footerItems = [],
  onNavigate,
  className,
}: SidebarContentProps) {
  const { t } = useTranslation("common");

  // A group whose items were all filtered out by the surface gate must not leave its heading
  // behind — an "ACQUIRE" label over empty space reads as a broken screen, not an empty one.
  const visibleGroups = groups.filter((group) => group.items.length > 0);

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-lg bg-surface-sidebar px-md py-xl",
        className
      )}
    >
      {/* "DZ Champions" is a proper noun — a brand name is not translated. */}
      <div className="flex shrink-0 items-center">
        <img
          src={images["logo-primary"]}
          alt="DZ Champions"
          className="h-8.5 w-auto"
        />
      </div>

      {visibleGroups.length > 0 ? (
        <nav
          aria-label={t("layout.main-navigation")}
          className="flex min-h-0 flex-1 flex-col gap-lg overflow-y-auto"
        >
          {visibleGroups.map((group) => (
            <div key={group.labelKey} className="flex flex-col gap-xxs">
              {/* Design "Label": xs/SemiBold gray/500, 8px inset — the heading sits over the
                  rows rather than being indented with them. */}
              <h2 className="px-xs py-xs text-xs-semibold text-(--text-tertiary)">
                {t(group.labelKey)}
              </h2>
              <NavLinks items={group.items} onNavigate={onNavigate} />
            </div>
          ))}
        </nav>
      ) : (
        // Design "Spacer": pushes the account card to the bottom when there is no nav to do it.
        <div className="min-h-0 flex-1" />
      )}

      {footerItems.length > 0 ? (
        <div className="shrink-0">
          <NavLinks items={footerItems} onNavigate={onNavigate} />
        </div>
      ) : null}

      <AccountMenu />
    </div>
  );
}
